import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { ModalSize } from "@via/utils/constants.tsx";
import { requiredValidation } from "@via/utils/form-validators";
import { formatFileSize } from "@via/utils/helpers";
import { SelectOption } from "@via/utils/interfaces";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import {
  useCancelDatasetStaging,
  useDatasetStagingList,
  useStartDatasetStaging,
} from "@via/pages/data/premium-dataset/premium-dataset-staging.api.tsx";
import {
  DataSourceProvider,
  DatasetStagingStatus,
  VmetaDataSetResponse,
} from "common";
import { type JSX } from "react";
import { useForm } from "react-hook-form";

type CloudOption = SelectOption & {
  metadata: { path: string; provider: DataSourceProvider };
};

const CLOUD_PROVIDERS = [DataSourceProvider.Google, DataSourceProvider.Amazon];

/** Only a bucket can hold a staged dataset, so SSH and URL data sources are not offered. */
export const cloudDataSourceOptions = (
  sources:
    | { id: number; name: string; path: string; provider: DataSourceProvider }[]
    | undefined
): CloudOption[] =>
  (sources ?? [])
    .filter(source => CLOUD_PROVIDERS.includes(source.provider))
    .map(source => ({
      label: source.name,
      value: source.id,
      metadata: { path: source.path, provider: source.provider },
    }));

export const stagingProgressLabel = (generation: {
  status: DatasetStagingStatus;
  progressPercentage: number | null;
  bytesTransferred?: number | null;
  objectCount: number;
  errorMessage: string | null;
}): string => {
  if (generation.status === DatasetStagingStatus.Completed)
    // A copy recorded before #933 carries no byte count, so the size is shown only when known.
    return generation.bytesTransferred
      ? `${generation.objectCount} files (${formatFileSize(generation.bytesTransferred)}) are in the cloud`
      : `${generation.objectCount} files are in the cloud`;
  if (generation.status === DatasetStagingStatus.Failed)
    return generation.errorMessage
      ? `Copy failed: ${generation.errorMessage}`
      : "Copy failed";
  if (generation.status === DatasetStagingStatus.Cancelled)
    return "Copy cancelled";
  if (generation.progressPercentage === null)
    return `Copying ${generation.objectCount} files`;
  return `Copying, ${generation.progressPercentage}% of ${generation.objectCount} files`;
};

interface Props {
  dataset: VmetaDataSetResponse | null;
  onClose: () => void;
}

const CopyToCloudModal = ({ dataset, onClose }: Props): JSX.Element => {
  const datasetId = dataset?._id ? String(dataset._id) : undefined;
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ dataSourceId: number | null }>({
    defaultValues: { dataSourceId: null },
  });
  const selectedDataSourceId = watch("dataSourceId");
  const { data: dataSourceList, isLoading: isDataSourceListLoading } =
    useGetDataSourceList();
  const { data: staging, isError: isStagingError } = useDatasetStagingList(
    datasetId,
    Boolean(dataset)
  );
  const { mutateAsync: startStaging, isPending } =
    useStartDatasetStaging(datasetId);
  const { mutate: cancelStaging } = useCancelDatasetStaging(datasetId);

  const options = cloudDataSourceOptions(dataSourceList);
  const running = staging?.generations.find(
    generation =>
      generation.status === DatasetStagingStatus.Pending ||
      generation.status === DatasetStagingStatus.InProgress
  );

  // Modal closes itself once onConfirm's promise resolves (see @via/components/modal), so the
  // request has to actually finish (mutateAsync) rather than just be fired (mutate). react-hook-form's
  // handleSubmit(fn) resolves normally on a failed validation too (it just skips fn and sets
  // `errors`, it does not throw), so plumbing that straight into onConfirm would close this modal on
  // an invalid submit exactly as it does on success. Wrapping it in a promise that only resolves once
  // the valid branch actually runs, and never otherwise, keeps the modal open on an invalid submit,
  // the same mechanism change-project-duplicate-run-modal.tsx's handleConfirmModal uses (a promise
  // that never resolves), here without needing a real <form> element to dispatch a submit event
  // through. The confirm button is also disabled with no destination selected (see
  // isConfirmDisabled below); this only-resolve-on-valid wrapper is the belt to that button's
  // suspenders, in case a future validation rule reopens the same gap. Mutation failures are
  // reported by the mutation's own onError notification and still resolve this promise (closing the
  // modal), since a failed *start* is a different problem than an invalid submit.
  const onConfirm = (): Promise<void> =>
    new Promise<void>(resolve => {
      void handleSubmit(async values => {
        if (!values.dataSourceId) return;
        try {
          await startStaging({ dataSourceId: values.dataSourceId });
        } finally {
          resolve();
        }
      })();
    });

  return (
    <Modal
      open={Boolean(dataset)}
      setOpen={onClose}
      title="Copy to Cloud"
      size={ModalSize.Large}
      confirmText="Copy"
      onConfirm={onConfirm}
      isConfirmLoading={isPending}
      isConfirmDisabled={
        isPending || Boolean(running) || !selectedDataSourceId
      }>
      <div className="my-4 space-y-4">
        <p className="text-sm text-gray-700">
          Copy every file in <strong>{dataset?.name}</strong> to one of your
          cloud data sources, so a Google Batch or AWS Batch run can read it.
          Files that already live in the cloud are left where they are.
        </p>

        <div>
          <label className="mb-2 block text-sm font-medium leading-6">
            Destination
            <span className="text-red-400"> *</span>
          </label>
          <SelectComponent
            control={control}
            name="dataSourceId"
            placeholder="Select a Google or Amazon data source"
            options={options}
            isLoading={isDataSourceListLoading}
            rules={requiredValidation("Destination")}
            noOptionsMessage="No Google or Amazon data source yet. Create one from Data Connections, then come back here."
          />
          {errors.dataSourceId && (
            <span className="text-xs text-rose-500">
              {errors.dataSourceId.message}
            </span>
          )}
        </div>

        {/* Spec section 6. The blind spot is documented next to the action rather than claimed away. */}
        <p className="text-xs text-gray-500">
          Foundry Connect notices when a file&apos;s size or modification time
          changes and copies it again. A file edited in place so that both stay
          the same looks unchanged, so copy the dataset again after editing one.
        </p>

        {isStagingError && (
          <p className="text-xs text-red-600">
            We could not load this dataset&apos;s copy history. Close and reopen
            this dialog to try again.
          </p>
        )}

        {staging?.generations.map(generation => (
          <div
            key={generation.generationKey}
            className="flex items-center justify-between rounded border p-2 text-sm">
            <span>{stagingProgressLabel(generation)}</span>
            {(generation.status === DatasetStagingStatus.Pending ||
              generation.status === DatasetStagingStatus.InProgress) && (
              <button
                type="button"
                className="text-xs underline"
                onClick={() => cancelStaging(generation.generationKey)}>
                Cancel
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default CopyToCloudModal;
